/* =============================================================================
   Materials — the ONE normalisation of a document's attachments.

   `PublicDocumentAttachment` is a storage shape: six kinds, each with a
   different subset of columns filled in, and three different places a title or a
   thumbnail might come from. Every surface that rendered it used to re-derive
   "what do I show here", which is exactly how a row and a tile end up disagreeing
   about the same file.

   So the derivation happens once, here, and produces a `Material`: a flat record
   that already knows its title, its sub-line, its poster, what happens on click,
   and whether it can be downloaded. The list view and the grid view then render
   the SAME records and cannot drift apart.
   ============================================================================= */
import { folderPath, folderSegmentSlug, folderSlug, isInFolder } from "@vedaunion/shared";
import type { PublicDocumentAttachment, DocumentScript } from "@vedaunion/shared";
import { resolveUrl } from "../../lib/platform";
import type { IconName } from "../ui/Icon";
import type { MediaSource } from "../media/MediaPlayer";

/** Display kinds. Narrower than the storage kinds: a video is a video whether it
 *  came from YouTube or from our own disk. */
export type MaterialKind = "pdf" | "audio" | "video" | "link" | "file";

export interface Material {
  id: string;
  kind: MaterialKind;
  /** What the person reads as the name of the thing. */
  title: string;
  /** The sub-line: script, recension, group, host. */
  meta: string;
  /** The trailing figure: a size or a duration. Empty when there is neither. */
  aside: string;
  /** Poster for the file itself, already platform-resolved. Null → typed glyph. */
  thumbnailUrl: string | null;
  /** Opened in a new tab (PDF, video, link). Null when the item only plays. */
  href: string | null;
  /** Direct download. Null for things we do not host (YouTube, external links). */
  downloadUrl: string | null;
  /** In-page audio. Null for everything that is not a recording. */
  audioUrl: string | null;
  /**
   * Plays in the video modal rather than leaving the site. Set for a
   * self-hosted recording AND for an unlisted YouTube one — the player takes
   * both, which is what lets the class videos move to YouTube later without
   * touching any of this.
   */
  videoSource: MediaSource | null;
  /** Shown with a play badge over the poster. */
  playable: boolean;
  /** Section this material belongs to — the first segment of `groupKey`. */
  group: string | null;
  /**
   * Where it lives: `groupKey` read as a folder path (`shared/src/folders.ts`).
   * `["Namakam", "Anuvāka 1"]` is the folder "Anuvāka 1" in the section
   * "Namakam"; `["Namakam"]` is loose in the section; `[]` is ungrouped.
   */
  path: string[];
  /** Bytes the download will cost; null for anything we do not host. */
  bytes: number | null;
  /** Upper-case extension for the typed placeholder ("PDF", "MP3"). */
  ext: string;
  icon: IconName;
}

const SCRIPT_LABEL: Record<DocumentScript, string> = {
  devanagari: "Devanāgarī",
  iast: "IAST",
  tamil: "Tamil",
  telugu: "Telugu",
  kannada: "Kannada",
  malayalam: "Malayalam",
  bengali: "Bengali",
  gurmukhi: "Gurmukhi",
  gujarati: "Gujarati",
  oriya: "Oriya",
  sinhala: "Sinhala",
  grantha: "Grantha",
  sharada: "Śāradā",
  tibetan: "Tibetan",
  latin: "Latin",
  other: "Other",
};

const ICON_FOR: Record<MaterialKind, IconName> = {
  pdf: "page",
  audio: "headphones",
  video: "file-video",
  link: "file-generic",
  file: "file-generic",
};

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  // A term of class recordings runs to gigabytes, and "2956.2 MB" on the
  // download button is a number nobody reads as three gigabytes.
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec) || sec < 0) return "";
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function extOf(url: string | null, fallback: string): string {
  if (!url) return fallback;
  const path = url.split(/[?#]/)[0] ?? "";
  const dot = path.lastIndexOf(".");
  const slash = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  if (dot <= slash + 1) return fallback;
  return path.slice(dot + 1).toUpperCase().slice(0, 4);
}

/**
 * YouTube's own still for a video id. `hqdefault` is the largest size that
 * exists for EVERY video — `maxresdefault` 404s on anything never published at
 * 720p+, which would leave a broken poster rather than a placeholder.
 */
function youtubeStill(id: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
}

function tidyHost(url: string | null): string {
  if (!url) return "Link";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Link";
  }
}

/**
 * Join the non-empty parts of a sub-line with the platform's middle dot,
 * leaving out any part the title already says. A recording titled
 * `Namakam · Anuvāka 1 · audio` in the section "Namakam" used to read
 * "Namakam · Namakam · Anuvāka 1" underneath — the same words three times.
 */
function metaLine(title: string, ...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => !!p && p.length > 0 && !title.includes(p))
    .join(" · ");
}

/**
 * One attachment → one Material, or null when the row cannot be rendered at all
 * (a PDF with no file, a YouTube reference with no id). Dropping it here is what
 * keeps every consumer free of `if (!a.fileUrl) return null` guards.
 */
export function toMaterial(a: PublicDocumentAttachment, slug: string): Material | null {
  const id = String(a.id);
  const path = folderPath(a.groupKey);
  const group = path[0] ?? null;
  const bytes = a.bytes ?? null;
  /**
   * Downloading goes through OUR origin, always.
   *
   * A `download` attribute on an <a> is honoured only for a same-origin URL,
   * and the files live on media.vedaunion.org. So the browser ignored the
   * attribute and navigated instead: tapping download on a recording opened
   * the m4a in a new tab and started playing it. The route below answers with
   * `Content-Disposition: attachment`, which is the only thing that makes a
   * browser save a file rather than render it.
   */
  const dl = resolveUrl(`/api/documents/${encodeURIComponent(slug)}/download/${a.id}`);
  // `resolveUrl`, not the raw path: in a packaged shell a bare "/library/x.pdf"
  // resolves against the WebView's own origin, not the API host.
  const thumb = a.thumbnailUrl ? resolveUrl(a.thumbnailUrl) : null;

  switch (a.kind) {
    case "pdf": {
      if (!a.fileUrl) return null;
      const url = resolveUrl(a.fileUrl);
      return {
        id,
        kind: "pdf",
        title: a.label,
        meta: metaLine(a.label, a.script ? SCRIPT_LABEL[a.script] : "PDF", a.description, group),
        aside: formatBytes(a.bytes),
        thumbnailUrl: thumb,
        href: url,
        downloadUrl: dl,
        audioUrl: null,
        videoSource: null,
        playable: false,
        group,
        path,
        bytes,
        ext: extOf(a.fileUrl, "PDF"),
        icon: ICON_FOR.pdf,
      };
    }
    case "audio": {
      if (!a.fileUrl) return null;
      const url = resolveUrl(a.fileUrl);
      return {
        id,
        kind: "audio",
        title: a.label,
        meta: metaLine(a.label, group, a.description, extOf(a.fileUrl, "AUDIO")),
        aside: formatDuration(a.durationSec),
        // Deliberately NO artwork, stored poster or not. A recording and a
        // class video were indistinguishable while both wore the same 16:9
        // plate, and the recordings DO carry generated plates in the database.
        // Audio reads as audio by its shape instead: a square tile with a
        // headphones mark, drawn in `index.css`.
        thumbnailUrl: null,
        href: null,
        downloadUrl: dl,
        audioUrl: url,
        videoSource: null,
        playable: true,
        group,
        path,
        bytes,
        ext: extOf(a.fileUrl, "MP3"),
        icon: ICON_FOR.audio,
      };
    }
    case "video": {
      if (!a.fileUrl) return null;
      const url = resolveUrl(a.fileUrl);
      return {
        id,
        kind: "video",
        title: a.label,
        meta: metaLine(a.label, group, a.description, extOf(a.fileUrl, "VIDEO")),
        aside: formatDuration(a.durationSec),
        thumbnailUrl: thumb,
        // No `href`: a self-hosted recording plays in the modal rather than
        // navigating away to a bare browser video view.
        href: null,
        downloadUrl: dl,
        audioUrl: null,
        videoSource: { kind: "file", url, mime: a.mime, media: "video" },
        playable: true,
        group,
        path,
        bytes,
        ext: extOf(a.fileUrl, "MP4"),
        icon: ICON_FOR.video,
      };
    }
    case "youtube_video": {
      if (!a.youtubeVideoId) return null;
      return {
        id,
        kind: "video",
        title: a.label || a.youtubeVideo?.title || "Video",
        meta: metaLine(a.label, "Video", group, a.description),
        aside: formatDuration(a.youtubeVideo?.durationSec ?? a.durationSec),
        // YouTube's own still, already a URL on their CDN — nothing of ours to
        // resolve. When the channel mirror has no record (an UNLISTED class
        // recording that sync never saw), fall back to the by-id thumbnail URL,
        // which YouTube serves for unlisted videos and costs no API quota. The
        // alternative was a `videos.list` call per attachment, and the daily
        // budget is 10,000 units against 1,600 for a single upload.
        thumbnailUrl:
          a.youtubeVideo?.thumbnailUrl ?? thumb ?? youtubeStill(a.youtubeVideoId),
        // A YouTube class plays in the SAME modal as a self-hosted one — same
        // player, same controls, same cover. This is the seam the later swap
        // turns on: change the attachment from `video` to `youtube_video` and
        // the surface does not change at all.
        href: null,
        // Not ours to hand out as a file.
        downloadUrl: null,
        audioUrl: null,
        videoSource: { kind: "youtube", videoId: a.youtubeVideoId },
        playable: true,
        group,
        path,
        bytes: null,
        ext: "YT",
        icon: ICON_FOR.video,
      };
    }
    case "youtube_playlist": {
      if (!a.youtubePlaylistId) return null;
      const n = a.youtubePlaylist?.itemCount;
      return {
        id,
        kind: "video",
        title: a.label || a.youtubePlaylist?.title || "Playlist",
        meta: metaLine(a.label, n ? `Playlist · ${n} videos` : "Playlist", group, a.description),
        aside: "",
        thumbnailUrl: a.youtubePlaylist?.thumbnailUrl ?? thumb,
        href: `https://www.youtube.com/playlist?list=${a.youtubePlaylistId}`,
        downloadUrl: null,
        audioUrl: null,
        videoSource: null,
        playable: true,
        group,
        path,
        bytes: null,
        ext: "YT",
        icon: ICON_FOR.video,
      };
    }
    case "external_link": {
      if (!a.externalUrl) return null;
      return {
        id,
        kind: "link",
        title: a.label,
        meta: metaLine(a.label, tidyHost(a.externalUrl), a.description),
        aside: "",
        thumbnailUrl: thumb,
        href: a.externalUrl,
        downloadUrl: null,
        audioUrl: null,
        videoSource: null,
        playable: false,
        group,
        path,
        bytes: null,
        ext: "URL",
        icon: ICON_FOR.link,
      };
    }
    case "file": {
      if (!a.fileUrl) return null;
      const url = resolveUrl(a.fileUrl);
      return {
        id,
        kind: "file",
        title: a.label,
        meta: metaLine(a.label, extOf(a.fileUrl, "FILE"), a.description, group),
        aside: formatBytes(a.bytes),
        thumbnailUrl: thumb,
        href: url,
        downloadUrl: dl,
        audioUrl: null,
        videoSource: null,
        playable: false,
        group,
        path,
        bytes,
        ext: extOf(a.fileUrl, "FILE"),
        icon: ICON_FOR.file,
      };
    }
    default:
      return null;
  }
}

export function toMaterials(atts: PublicDocumentAttachment[], slug: string): Material[] {
  return atts
    .map((a) => toMaterial(a, slug))
    .filter((m): m is Material => m !== null);
}

/* -- Tabs ------------------------------------------------------------------ */

export type MaterialTab = "all" | MaterialKind | "about";

const TAB_LABEL: Record<Exclude<MaterialTab, "all" | "about">, string> = {
  pdf: "Texts",
  audio: "Audio",
  video: "Video",
  file: "Files",
  link: "Links",
};

/** Display order of the kind tabs. */
const TAB_ORDER: Array<Exclude<MaterialTab, "all" | "about">> = [
  "pdf",
  "audio",
  "video",
  "file",
  "link",
];

export interface TabDef {
  key: MaterialTab;
  label: string;
  count: number | null;
}

/**
 * The tab strip for a document, derived from what the document actually has.
 * A kind with no materials gets no tab — the alternative, a fixed row of tabs
 * where four of six are empty, is chrome that lies about the contents.
 *
 * Inside a folder the strip is built from the folder's own materials and has
 * no About: the essay belongs to the document, not to a folder of it.
 */
export function buildTabs(materials: Material[], { about = true }: { about?: boolean } = {}): TabDef[] {
  const kinds: TabDef[] = [];
  for (const kind of TAB_ORDER) {
    const n = materials.filter((m) => m.kind === kind).length;
    if (n > 0) kinds.push({ key: kind, label: TAB_LABEL[kind], count: n });
  }
  const tail: TabDef[] = about ? [{ key: "about", label: "About", count: null }] : [];

  // Nothing attached: no browser, no empty "All materials 0" tab — just the text.
  if (kinds.length === 0) return tail;
  // One kind: "All materials" and that kind would list exactly the same files.
  if (kinds.length === 1) return [...kinds, ...tail];
  return [{ key: "all", label: "All materials", count: materials.length }, ...kinds, ...tail];
}

/**
 * A folder, as a tile: everything under one path, at any depth, within the
 * current tab. `key` is its URL value (`namakam/anuvaka-1`), so a tile, a link
 * and a breadcrumb all name it the same way.
 */
export interface MaterialFolder {
  key: string;
  title: string;
  path: string[];
  items: Material[];
}

export interface MaterialGroup {
  key: string;
  title: string;
  /** Drawn first, as on any file system: folders, then loose files. */
  folders: MaterialFolder[];
  items: Material[];
}

const KIND_GROUP_TITLE: Record<MaterialKind, string> = {
  pdf: "Texts & PDFs",
  audio: "Recordings",
  video: "Classes & talks",
  file: "Files",
  link: "Elsewhere",
};

const inTab = (m: Material, tab: MaterialTab) => tab === "all" || tab === "about" || m.kind === tab;

/** Everything inside the folder named by `slugs` (all of it when `slugs` is empty). */
export function materialsIn(materials: Material[], slugs: readonly string[]): Material[] {
  return slugs.length === 0 ? materials : materials.filter((m) => isInFolder(m.path, slugs));
}

/**
 * Split what a tab shows at one place in the tree into titled sections,
 * preserving the editor's order.
 *
 * At the top (`at` empty) a section is the first segment of the path —
 * "Namakam", "Camakam" — and anything one level deeper collapses into a folder
 * tile inside its section. Without a group, items fall into a section per kind,
 * which is what makes the "All" tab readable.
 *
 * Inside a folder (`at` = its path) the folder IS the heading, so its own
 * sub-folders come first, as tiles under "Folders", and its files follow in
 * sections per kind in ONE fixed order — texts, then recordings, then classes
 * (`TAB_ORDER`). A folder is opened to study from: the text is what a class
 * reads along with, so it leads, whatever order the files were uploaded in.
 */
export function groupMaterials(
  materials: Material[],
  tab: MaterialTab,
  at: readonly string[] = [],
): MaterialGroup[] {
  const depth = at.length;
  const atSlugs = at.map(folderSegmentSlug);
  const order: string[] = [];
  const buckets = new Map<string, MaterialGroup>();
  const bucket = (key: string, title: string) => {
    let b = buckets.get(key);
    if (!b) {
      b = { key, title, folders: [], items: [] };
      buckets.set(key, b);
      order.push(key);
    }
    return b;
  };

  for (const m of materialsIn(materials, atSlugs)) {
    if (!inTab(m, tab)) continue;
    const rel = m.path.slice(depth);
    let b: MaterialGroup;
    let folder: string[] | null = null;
    if (depth === 0) {
      b = rel[0] ? bucket(`group:${folderSegmentSlug(rel[0])}`, rel[0]) : bucket(`kind:${m.kind}`, KIND_GROUP_TITLE[m.kind]);
      if (rel.length >= 2) folder = m.path.slice(0, 2);
    } else if (rel.length > 0) {
      b = bucket("folders", "Folders");
      folder = m.path.slice(0, depth + 1);
    } else {
      b = bucket(`kind:${m.kind}`, KIND_GROUP_TITLE[m.kind]);
    }

    if (!folder) {
      b.items.push(m);
      continue;
    }
    const key = folderSlug(folder);
    let f = b.folders.find((x) => x.key === key);
    if (!f) {
      f = { key, title: folder[folder.length - 1]!, path: folder, items: [] };
      b.folders.push(f);
    }
    f.items.push(m);
  }
  if (depth > 0) {
    const rank = (key: string) =>
      key === "folders" ? -1 : TAB_ORDER.indexOf(key.slice("kind:".length) as MaterialKind);
    order.sort((a, b) => rank(a) - rank(b));
  }
  return order.map((k) => buckets.get(k)!);
}

const KIND_NOUN: Record<MaterialKind, [one: string, many: string]> = {
  video: ["video", "videos"],
  audio: ["recording", "recordings"],
  pdf: ["text", "texts"],
  file: ["file", "files"],
  link: ["link", "links"],
};

/** "2 texts · 4 recordings · 2 videos" — what a folder holds, in the order it shows it. */
export function describeContents(items: Material[]): string {
  const parts: string[] = [];
  for (const kind of TAB_ORDER) {
    const n = items.filter((m) => m.kind === kind).length;
    if (n > 0) parts.push(`${n} ${KIND_NOUN[kind][n === 1 ? 0 : 1]}`);
  }
  return parts.join(" · ");
}

/** What downloading these would cost, counting only files we host. */
export function totalBytes(items: Material[]): number {
  return items.reduce((sum, m) => sum + (m.downloadUrl ? m.bytes ?? 0 : 0), 0);
}
